import { Star, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { shortName } from "@/lib/names";
import { Container, Section, SectionHeading } from "@/components/site/Bits";

const Stars = ({ score = 5 }) => (
  <span className="flex items-center gap-0.5" aria-label={`${score} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, index) => (
      <Star key={index}
            className={`h-4 w-4 ${index < score ? "fill-[#F5A623] text-[#F5A623]" : "text-pg-border"}`} />
    ))}
  </span>
);

const initials = (name = "") => name.split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2);

/** One review card, shared by the homepage and the About page so both read the same. */
export const GoogleReviewQuote = ({ review, url = "" }) => (
  <figure className="pg-card flex h-full flex-col p-7" data-testid={`review-${review.id}`}>
    <Stars score={review.rating || 5} />
    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-pg-text">
      <span className="line-clamp-[12]">“{review.body}”</span>
      {url && review.body.length > 420 && (
        <a href={url} target="_blank" rel="noreferrer"
           className="pg-tap mt-2 text-xs font-semibold text-pg-primary hover:underline">
          Read the full review on Google
        </a>
      )}
    </blockquote>
    <figcaption className="mt-5 flex items-center gap-3 border-t border-pg-border pt-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pg-pale text-xs font-bold text-pg-primary">
        {initials(review.customer_name)}
      </span>
      <span className="text-xs text-pg-muted">
        <span className="block font-semibold text-pg-dark">{shortName(review.customer_name)}</span>
        {[review.location, review.service].filter(Boolean).join(" · ")}
        {review.source === "Google" && (
          <span className="mt-0.5 block text-[11px] text-pg-muted">Verified Google review</span>
        )}
      </span>
    </figcaption>
  </figure>
);

/** Google reviews on the homepage: the rating badge, recent reviews and a link to the profile. */
export default function GoogleReviews({ reviews = [], settings }) {
  const url = settings?.google_review_url || "";
  const rating = Number(settings?.google_rating || 0);
  const count = Number(settings?.google_review_count || 0);
  const shown = reviews.slice(0, 3);
  if (shown.length === 0 && !rating) return null;

  return (
    <Section pale testId="home-google-reviews">
      <SectionHeading
        eyebrow="Customer reviews"
        title="What our customers say"
        intro="Reviews from genuine completed work. We do not publish invented reviews."
      />

      <div className="mt-8 flex flex-col gap-5 rounded-xl border border-pg-border bg-white p-6 md:flex-row md:items-center md:justify-between"
           data-testid="google-rating-card">
        <div className="flex items-center gap-4">
          <img src="/logo-google.webp" alt="Google" className="h-8 w-auto" loading="lazy" />
          <div>
            <div className="flex items-center gap-2">
              {rating > 0 ? (
                <>
                  <span className="text-2xl font-extrabold text-pg-dark" data-testid="google-rating">
                    {rating.toFixed(1)}
                  </span>
                  <Stars score={Math.round(rating)} />
                </>
              ) : (
                <span className="text-sm font-bold text-pg-dark">Reviews on Google</span>
              )}
            </div>
            <p className="mt-1 text-xs text-pg-muted">
              {count > 0
                ? `Based on ${count} Google review${count === 1 ? "" : "s"}`
                : "Read every review on our Google profile"}
            </p>
          </div>
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" data-testid="google-reviews-link"
             className="inline-flex items-center gap-2 rounded-md border border-pg-border px-5 py-3 text-sm font-semibold text-pg-dark transition-colors hover:border-pg-primary">
            Read our reviews on Google <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}      </div>

      {shown.length > 0 && (
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {shown.map((review) => (
            <GoogleReviewQuote key={review.id} review={review} url={url} />
          ))}
        </div>
      )}
      <Container className="px-0">
        <div className="mt-6">
          <Link to="/reviews" data-testid="all-reviews-link"
                className="inline-flex items-center gap-2 rounded-md bg-pg-primary px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-pg-dark">
            Read all customer reviews
          </Link>
        </div>
        <p className="mt-5 text-xs text-pg-muted">
          Reviews are published by our team from completed work. Ratings shown are maintained from our
          Google, Checkatrade and TrustATrader profiles.
        </p>
      </Container>
    </Section>
  );
}
